import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isForwardDeal, isOptionDeal } from '@src/types/OTC/otcGuards';
import {
  Button,
  ButtonVariants,
  DrawerFooter,
  FormControlSizes,
  useAppwideDrawerContext,
  type BoxProps,
} from '@talos/kyoko';
import { ScrollableBox } from '@talos/kyoko/src/components/ScrollableBox';
import { useCallback } from 'react';
import styled from 'styled-components';
import { FixedRateLoanInstrumentInspectorSections } from './FixedRateLoanInstrumentInspectorSections';
import { FloatingRateLoanInstrumentInspectorSections } from './FloatingRateLoanInstrumentInspectorSections';
import { ForwardInstrumentInspectorSections } from './ForwardInstrumentInspectorSections';
import { InspectorBasicInfo } from './InspectorBasicInfo';
import { OptionInstrumentInspectorSections } from './OptionInstrumentInspectorSections';

export const overflowOuterProps: BoxProps = { h: '100%', overflow: 'auto' };
export const overflowInnerProps: BoxProps = {
  m: 'spacing16',
};

const instrumentInspectorScrollOuterProps: BoxProps = { flex: 1, minHeight: 0, overflow: 'auto' };

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

interface InstrumentInspectorDrawerProps {
  instrumentData: OTCDealData;
}

const InstrumentInspectorDrawerSections = ({ instrumentData }: { instrumentData: OTCDealData }) => {
  if (isForwardDeal(instrumentData)) {
    return <ForwardInstrumentInspectorSections instrumentData={instrumentData} />;
  }

  if (isFixedRateLoanDeal(instrumentData)) {
    return <FixedRateLoanInstrumentInspectorSections instrumentData={instrumentData} />;
  }

  if (isFloatingRateLoanDeal(instrumentData)) {
    return <FloatingRateLoanInstrumentInspectorSections instrumentData={instrumentData} />;
  }

  if (isOptionDeal(instrumentData)) {
    return <OptionInstrumentInspectorSections instrumentData={instrumentData} />;
  }

  return null;
};

export const InstrumentInspectorDrawer = ({ instrumentData }: InstrumentInspectorDrawerProps) => {
  const { closeDrawer } = useAppwideDrawerContext();

  const handleClose = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <ContentWrapper data-testid="instrument-inspector-drawer">
      <InspectorBasicInfo instrumentData={instrumentData} />
      <ScrollableBox outerProps={instrumentInspectorScrollOuterProps} innerProps={overflowInnerProps}>
        <InstrumentInspectorDrawerSections instrumentData={instrumentData} />
      </ScrollableBox>

      <DrawerFooter data-testid="instrument-inspector-drawer-footer" w="100%" gap="12px" p="12px">
        <Button
          style={{ padding: '8px 12px' }}
          variant={ButtonVariants.Default}
          size={FormControlSizes.Small}
          onClick={handleClose}
        >
          Close
        </Button>
      </DrawerFooter>
    </ContentWrapper>
  );
};
